<template>
  <div class="relative">
    <ImpactFamilyEmptyBackground
      v-if="family.family.length === 0 && !!emptyState && !!rootPosition"
      :style="{
        transform: `translate(calc(-50% + ${rootPosition.x}px),calc(-50% + ${rootPosition.y}px)) scale(0.85)`,
      }"
      class="pointer-events-none absolute"
    />
    <div ref="container" class="h-full w-full" />

    <div
      v-if="enablePanAndZoom"
      class="absolute bottom-2 left-2 flex items-center gap-1 rounded-xl bg-navy p-2"
    >
      <button
        type="button"
        class="rounded-full bg-white p-1.5 text-navy lg:p-2.5"
        @click="reduceZoom"
      >
        <IconsRemove class="h-4 w-4 lg:h-5 lg:w-5" />
      </button>

      <span
        class="inline-block w-[105px] text-center font-Aeonik text-[14px] font-bold text-white"
        >{{ percentageFormatter.format(zoom / BASE_ZOOM) }} Zoom</span
      >

      <button
        type="button"
        class="rounded-full bg-white p-1.5 text-navy lg:p-2.5"
        @click="increaseZoom"
      >
        <IconsAdd class="h-4 w-4 lg:h-5 lg:w-5" />
      </button>
    </div>
    <template v-if="Graph">
      <PopoverRoot v-if="!!openNode" :open="!!openNode">
        <PopoverTrigger
          :style="{
            transform: `translate(${Graph.graph2ScreenCoords(openNode.x, openNode.y).x}px, ${Graph.graph2ScreenCoords(openNode.x, openNode.y).y}px)`,
          }"
          class="absolute left-0 top-0 z-[100]"
        />

        <PopoverContent
          :key="openNode?.id"
          align="end"
          side="top"
          avoid-collisions
          :arrow-padding="20"
          :side-offset="4.5 * Graph.zoom()"
          class="z-[100] rounded-xl border-2 border-navy bg-white p-4 text-navy"
        >
          <PopoverArrow :width="25" :height="16" class="fill-navy" />
          <PopoverArrow
            :width="25"
            :height="16"
            class="-translate-y-1 fill-white"
          />
          <ImpactFamilyMemberPopoverContent :member="openNode ?? undefined" />
        </PopoverContent>
      </PopoverRoot>

      <PopoverRoot
        v-if="family.family.length === 0 && !!emptyState && rootPosition"
        open
      >
        <PopoverTrigger
          :style="{
            transform: `translate(${rootPosition.x}px, ${rootPosition.y}px)`,
          }"
          class="absolute left-0 top-0 z-[100]"
        />

        <PopoverContent
          :key="`${rootPosition.x}-${rootPosition.y}`"
          align="center"
          side="bottom"
          avoid-collisions
          :side-offset="Graph.nodeRelSize() * 4 * Graph.zoom()"
          class="z-[100] w-[278px] rounded-xl border-2 border-navy bg-white p-6 text-navy"
        >
          <PopoverArrow :width="25" :height="16" class="fill-navy" />
          <PopoverArrow
            :width="25"
            :height="16"
            class="-translate-y-1 fill-white"
          />
          <h2 class="text-center text-[15px] font-bold uppercase leading-none">
            You don't have anyone in your Impact Family yet.
          </h2>

          <div class="mt-6 flex items-center gap-2.5">
            <Box
              class="h-[33px] w-[33px] shrink-0 bg-white"
              rounded="full"
              border="lg"
            >
              <button
                type="button"
                class="flex h-full w-full items-center justify-center"
                @click="video?.play()"
              >
                <IconsQuestionMark class="h-4 w-4" />
              </button>
            </Box>
            <ProfileImpactCopyReferralButton
              class="border border-navy !px-0 !py-1.5 shadow-[2px_2px_0px_0px] shadow-navy"
              :referral-code="emptyState.referralCode"
            />
          </div>
          <VideoPlayerModal id="481769494" ref="video" />
        </PopoverContent>
      </PopoverRoot>
    </template>
  </div>
</template>

<script setup lang="ts">
import type ForceGraphInstance from "force-graph";
import type { NodeObject } from "force-graph";

import { forceLink } from "d3";
import VideoPlayerModal from "~/components/VideoPlayer/Modal.vue";

const ForceGraph = (await import("force-graph")).default;

const BASE_ZOOM = 5;
const LINK_WIDTH = 0.4;

type FamilyTree = {
  id: number;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  family: FamilyTree[];
};

const props = withDefaults(
  defineProps<{
    family: FamilyTree;
    emptyState?: {
      referralCode: string;
    };
    offsetY?: number;
  }>(),
  {
    offsetY: 0,
    emptyState: undefined,
  },
);
const family = toRef(props, "family");

const data = computed(() => {
  const nodes = [];
  const links: { source: number; target: number; level: number }[] = [];
  const stack = [{ data: family.value, level: 0 }];
  while (stack.length) {
    const next = stack.pop()!;
    const img = new Image();
    img.src = next.data.avatarUrl ?? "/images/avatars/default-avatar.jpg";
    img.style.objectFit = "cover";
    const node = {
      id: next.data.id,
      level: next.level,
      img,
      firstName: next.data.firstName,
      lastName: next.data.lastName,
    };
    nodes.push(node);

    for (const child of next.data.family) {
      links.push({ source: next.data.id, target: child.id, level: next.level });
      stack.push({
        data: child,
        level: next.level + 1,
      });
    }
  }

  return {
    nodes,
    links,
    nodesById: Object.fromEntries(nodes.map((n) => [n.id, n])),
  };
});

const zoom = ref(BASE_ZOOM);

function increaseZoom() {
  const step = BASE_ZOOM / 4; // 25%
  const nextZoom = Math.floor(zoom.value / step) * step + step;

  Graph.value?.zoom(nextZoom);
}

function reduceZoom() {
  const step = BASE_ZOOM / 4; // 25%
  const nextZoom = Math.ceil(zoom.value / step) * step - step;

  Graph.value?.zoom(nextZoom);
}

const enablePanAndZoom = computed(() => family.value.family.length > 0);

const openNode = ref<{
  id: number;
  x: number;
  y: number;
  firstName: string;
  lastName: string;
} | null>(null);

const container = ref<HTMLElement | null>(null);
const Graph = ref<ForceGraphInstance | null>(null);
const graphOffsetY = ref(0);

watch(container, (c) => {
  if (!c) return;

  Graph.value = new ForceGraph(c);
  Graph.value
    .graphData(data.value)
    .d3Force(
      "link",
      forceLink().distance((l) => {
        // @ts-expect-error - Level is not in the d3 link type but is passed in data
        const level: number = l.level;
        return (Graph.value?.nodeRelSize() ?? 1) * (level === 0 ? 4 : 1);
      }),
    )
    .linkCanvasObject((link, ctx) => {
      const start = link.source as NodeObject | undefined;
      const end = link.target as NodeObject | undefined;
      if (!start || !end || !start.x || !end.x) return; // skip invalid link

      ctx.beginPath();
      ctx.lineWidth = LINK_WIDTH;
      ctx.strokeStyle = "#16114F55";
      ctx.moveTo(start.x, (start.y ?? 0) + graphOffsetY.value);
      ctx.lineTo(end.x, (end.y ?? 0) + graphOffsetY.value);
      ctx.stroke();
    })
    .linkPointerAreaPaint((link, color, ctx) => {
      const start = link.source as NodeObject | undefined;
      const end = link.target as NodeObject | undefined;
      if (!start || !end || !start.x || !end.x) return; // skip invalid link

      ctx.beginPath();
      ctx.lineWidth = LINK_WIDTH;
      ctx.moveTo(start.x, (start.y ?? 0) + graphOffsetY.value);
      ctx.lineTo(end.x, (end.y ?? 0) + graphOffsetY.value);
      ctx.strokeStyle = color;
      ctx.stroke();
    })
    .nodeCanvasObject((node, ctx) => {
      const n = data.value.nodesById[node.id!];
      const size =
        n.level === 0
          ? Graph.value!.nodeRelSize() * 3
          : Graph.value!.nodeRelSize();

      const x = node.x ?? 0;
      const y = (node.y ?? 0) + graphOffsetY.value;
      const imgSize = size * 2;

      // bg
      ctx.beginPath();
      ctx.arc(x, y, size, 0, 2 * Math.PI);
      ctx.fillStyle = "#16114F";
      ctx.fill();

      // shadow
      ctx.beginPath();
      ctx.arc(x + size * 0.04, y + size * 0.04, size, 0, 2 * Math.PI);
      ctx.fillStyle = "#16114F";
      ctx.fill();

      // border
      ctx.arc(x, y, size, 0, 2 * Math.PI);
      ctx.strokeStyle = "#16114F";
      ctx.lineWidth = 0.3;
      ctx.stroke();

      // rounded image
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, size, 0, 2 * Math.PI);
      ctx.clip();
      ctx.drawImage(n.img, x - imgSize / 2, y - imgSize / 2, imgSize, imgSize);
      ctx.restore();
    })
    .nodePointerAreaPaint((node, color, ctx) => {
      const n = data.value.nodesById[node.id!];
      const size =
        n.level === 0
          ? Graph.value!.nodeRelSize() * 3
          : Graph.value!.nodeRelSize();

      const x = node.x ?? 0;
      const y = (node.y ?? 0) + graphOffsetY.value;
      ctx.beginPath();
      ctx.arc(x, y, size * 1.05, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();
    })
    .enableNodeDrag(false)
    .onNodeClick((node) => {
      if (!node.id || node.id === family.value.id) return;
      const id = Number(node.id);
      if (openNode.value?.id === id) {
        openNode.value = null;
      } else {
        openNode.value = {
          id,
          x: node.x ?? 0,
          y: (node.y ?? 0) + graphOffsetY.value,
          firstName: data.value.nodesById[id].firstName,
          lastName: data.value.nodesById[id].lastName,
        };
      }
    })
    .onLinkClick(() => {
      openNode.value = null;
    })
    .onZoom((transform) => {
      zoom.value = transform.k;
      openNode.value = null;
    })
    .onBackgroundClick(() => {
      openNode.value = null;
    })
    .warmupTicks(200)
    .minZoom(BASE_ZOOM / 10)
    .zoom(BASE_ZOOM)
    .enablePanInteraction(enablePanAndZoom.value)
    .enableZoomInteraction(enablePanAndZoom.value);
});

const percentageFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const rootPosition = ref<{ x: number; y: number } | undefined>();

const { width, height } = useElementSize(container);
watchEffect(() => {
  if (!Graph.value) return;
  Graph.value.width(width.value).height(height.value);

  setTimeout(() => {
    const root = Graph.value
      ?.graphData()
      .nodes.find((n) => n.id === family.value.id);
    Graph.value?.centerAt(root?.x ?? 0, root?.y ?? 0).zoom(BASE_ZOOM);

    setTimeout(() => {
      if (!Graph.value) return;
      const offset = Graph.value.screen2GraphCoords(
        width.value / 2,
        height.value / 2 + props.offsetY,
      );
      graphOffsetY.value = offset.y;
      rootPosition.value = Graph.value?.graph2ScreenCoords(
        root?.x ?? 0,
        (root?.y ?? 0) + offset.y,
      ) ?? { x: 0, y: 0 };
    }, 100);
  }, 50);
});

const video = ref<InstanceType<typeof VideoPlayerModal> | null>(null);
</script>
